import { Dispatch, FC, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { EpicMoversParamType, TopGainerAndLoserData } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import {
  epicMoversFilterOption,
  setEpicMoversFilterOption,
} from '../../lib/store/discover/epicMoversState';
import { getEpicMovers as getEpicMoversCall } from '../../lib/store/fundamental/index';
import {
  epicMoversOptionToString,
  getEpicMoversOptionByStringValue,
} from '../../lib/store/fundamental/mappers';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { defaultLogoBase64 } from '../../lib/util/assets/logos';
import { getLimitedText } from '../../lib/util/DataHelpers';
import {
  isCallStatusError,
  isCallStatusReady,
} from '../../lib/util/error-handling/StatusByCallHelpers';
import { isEmpty } from '../../lib/util/typeGuards';

import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import EpicMovers from '../../shared_components/EpicMovers/EpicMovers';
import {
  EPIC_MOVERS_ROW_COUNT,
  EPIC_MOVERS_SYMBOL_NAME_LIMIT,
} from '../../shared_components/EpicMovers/EpicMovers.constants';
import { EpicMoversRow } from '../../shared_components/EpicMovers/EpicMovers.types';
import { UI_ROUTES } from '../../util/routes';


const EpicMoversWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    getEpicMovers,
    isEpicMoversReady,
    isEpicMoversError,
    epicMoversData,
    companyLogoAndName,
  } = props;
  const isRequestCompleted = isEpicMoversReady || isEpicMoversError;
  const { responseData } = epicMoversData;
  const { topGainers, topLosers } = responseData ?? {};
  const hasNoAnyData = isEmpty(topGainers) && isEmpty(topLosers) && isRequestCompleted;

  const navigate = useNavigate();
  const optionValue = epicMoversFilterOption.get();

  // Single market: the row's market fields are constants here, not resolved per symbol.
  const toRows = useMemo(() => (items: TopGainerAndLoserData[] = []): EpicMoversRow[] => (
    items.map((item) => {
      const entry = companyLogoAndName[item.symbol];

      return {
        ...item,
        lookupSymbol: item.symbol,
        currencySymbol: CurrencySymbolEnum.USD,
        isEuropeanSymbol: false,
        logo: entry?.logo ?? defaultLogoBase64,
        url: entry?.url ?? item.url ?? '',
        displayName: getLimitedText(item.name ?? entry?.name, EPIC_MOVERS_SYMBOL_NAME_LIMIT),
      };
    })
  ), [companyLogoAndName]);

  const gainerRows = useMemo(() => toRows(topGainers), [toRows, topGainers]);
  const loserRows = useMemo(() => toRows(topLosers), [toRows, topLosers]);

  const handleFilterOption = (_range: number[], valueOption: string) => {
    const newOptionValue = getEpicMoversOptionByStringValue(valueOption);
    setEpicMoversFilterOption(newOptionValue);
    getEpicMovers({ range: newOptionValue });
  };

  const handleViewAllClick = (isTopGainersSelected: boolean) => {
    navigate(
      { pathname: UI_ROUTES.discover + UI_ROUTES.epicMoversToday },
      { state: { isTopGainersSelected } },
    );
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const symbolsToSubscribe = useMemo(() => {
    const gainers = topGainers?.slice(0, EPIC_MOVERS_ROW_COUNT) || [];
    const losers = topLosers?.slice(0, EPIC_MOVERS_ROW_COUNT) || [];
    const allSymbols = [...gainers, ...losers];

    return allSymbols.map((item) => ({
      symbol: item.symbol,
      stockExchangeCode: item.stockExchangeCode,
      countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
    }));
  }, [topGainers, topLosers, companyLogoAndName]);

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: isRequestCompleted && !hasNoAnyData && symbolsToSubscribe.length > 0,
  });

  useEffect(() => {
    getEpicMovers({ range: optionValue });
  }, [getEpicMovers, optionValue]);

  return (
    <EpicMovers
      topGainers={gainerRows}
      topLosers={loserRows}
      isPending={!isRequestCompleted}
      isReady={isEpicMoversReady}
      filterOptionValue={epicMoversOptionToString(optionValue)}
      onFilterChange={handleFilterOption}
      onViewAllClick={handleViewAllClick}
      onSymbolClick={handleSymbolClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isEpicMoversReady: isCallStatusReady(
    FundamentalCall.getEpicMovers,
    state.fundamental.statusByCall,
  ),
  isEpicMoversError: isCallStatusError(
    FundamentalCall.getEpicMovers,
    state.fundamental.statusByCall,
  ),
  epicMoversData: state.fundamental.epicMoversData,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getEpicMovers: (payload: EpicMoversParamType) => dispatch(getEpicMoversCall(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EpicMoversWrapper);
